import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Spinner,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

/**
 * Admin page that lists every user on the system together with the
 * resources they have access to:
 *
 *  - Non-public models they have been granted visibility on
 *    (admin can add/remove grants).
 *  - Trading strategies they own (read-only summary, each row links
 *    to the public view page at <c>/trading-strategies/:id/view</c>).
 *
 * Strategies aren't permissioned per-user (only the owner / a connector
 * change controls who can act on them) so this panel is informational —
 * it lets an admin audit at a glance which strategies exist for each
 * user without having to flip the "All / Only mine" toggle on the
 * public Trading Strategies page.
 */
export const AdminUserAccess = () => {
  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [addModalUser, setAddModalUser] = useState(null);
  const [addModalSearch, setAddModalSearch] = useState('');
  const [addModalError, setAddModalError] = useState(null);

  const [pendingGrant, setPendingGrant] = useState({}); // userId -> modelId being added
  const [pendingRevoke, setPendingRevoke] = useState({}); // userId-modelId -> bool

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // searchUsers returns a paginated range; we fetch enough for any
      // realistic admin tenant. The endpoint is admin-gated server-side.
      //
      // For models we use /api/models/list (admin-gated, returns every
      // model with its VisibleToUserIds array). For strategies we hit
      // /api/trading-strategies with mineOnly=false — admins receive
      // every strategy, non-admins would be restricted to their own
      // (the page is admin-gated upstream so this is purely defensive).
      const [usersResult, modelsList, strategiesList] = await Promise.all([
        apiClient.searchUsers({ Limit: 10000, Offset: 0, SkipTotalCount: true }),
        apiClient.getModelsList(),
        apiClient.getTradingStrategies({ mineOnly: false }),
      ]);
      setUsers((usersResult && usersResult.Items) || []);
      setModels(modelsList || []);
      setStrategies(strategiesList || []);
    } catch (err) {
      console.error('Failed to load user-access data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Strategies that have at least one explicit owner — ownerless
  // strategies are normally not possible (the controller forces
  // UserId = LoggedUserId on create) but we filter defensively so the
  // UI never tries to bucket a strategy under no-one.
  const strategiesWithOwners = useMemo(() => {
    return strategies.filter(s => s.UserId != null);
  }, [strategies]);

  // Models that have at least one explicit user grant. Public models
  // are visible to everyone regardless of the array, so the page only
  // surfaces rows that actually carry permission state.
  const modelsWithGrants = useMemo(() => {
    return models.filter(m => m.VisibleToUserIds && m.VisibleToUserIds.length > 0);
  }, [models]);

  // Reverse lookup: user id -> [model, ...]
  const visibleModelsByUser = useMemo(() => {
    const map = {};
    for (const m of modelsWithGrants) {
      for (const uid of m.VisibleToUserIds || []) {
        if (!map[uid]) map[uid] = [];
        map[uid].push(m);
      }
    }
    return map;
  }, [modelsWithGrants]);

  // Reverse lookup: user id -> [strategy, ...]
  const strategiesByUser = useMemo(() => {
    const map = {};
    for (const s of strategiesWithOwners) {
      if (!map[s.UserId]) map[s.UserId] = [];
      map[s.UserId].push(s);
    }
    // Sort newest first within each user's bucket to match the public
    // Trading Strategies page (which uses ORDER BY Id DESC).
    for (const uid of Object.keys(map)) {
      map[uid].sort((a, b) => b.Id - a.Id);
    }
    return map;
  }, [strategiesWithOwners]);

  async function handleAdd(userId, modelId) {
    const key = `${userId}-${modelId}`;
    setPendingGrant(prev => ({ ...prev, [userId]: modelId }));
    setAddModalError(null);
    try {
      await apiClient.addVisibleUser(modelId, userId);
      // Optimistic merge — push the new grant onto the model's array so
      // the user row updates without reloading the whole page.
      setModels(prev => prev.map(m => {
        if (m.Id !== modelId) return m;
        const current = m.VisibleToUserIds || [];
        if (current.includes(userId)) return m;
        return { ...m, VisibleToUserIds: [...current, userId] };
      }));
      setAddModalUser(null);
      setAddModalSearch('');
    } catch (err) {
      console.error('Add visible user failed', err);
      setAddModalError(err.message || err.toString());
    } finally {
      setPendingGrant(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      // intentionally leave key so we don't double-trigger
      void key;
    }
  }

  async function handleRemove(userId, modelId) {
    const key = `${userId}-${modelId}`;
    if (pendingRevoke[key]) return;
    setPendingRevoke(prev => ({ ...prev, [key]: true }));
    try {
      await apiClient.removeVisibleUser(modelId, userId);
      setModels(prev => prev.map(m => {
        if (m.Id !== modelId) return m;
        return { ...m, VisibleToUserIds: (m.VisibleToUserIds || []).filter(u => u !== userId) };
      }));
    } catch (err) {
      console.error('Remove visible user failed', err);
      alert('Failed to revoke grant: ' + (err.message || err));
    } finally {
      setPendingRevoke(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function describeUser(u) {
    const id = u.Id;
    const label = u.Name || u.Email || `User #${id}`;
    return { id, label, email: u.Email || '', roles: u.Roles || [] };
  }

  // Filter candidate models for the "add" modal:
  //  - non-public (public models don't need explicit grants)
  //  - not already granted to this user
  //  - matches the search box (by id / name / file name, case-insensitive)
  const candidatesForUser = useCallback((userId) => {
    const q = addModalSearch.trim().toLowerCase();
    return models
      .filter(m => !m.Public)
      .filter(m => !(m.VisibleToUserIds || []).includes(userId))
      .filter(m => {
        if (!q) return true;
        return (
          (m.Name && m.Name.toLowerCase().includes(q)) ||
          (m.FileName && m.FileName.toLowerCase().includes(q)) ||
          String(m.Id).includes(q)
        );
      })
      .sort((a, b) => (a.Name || a.FileName || '').localeCompare(b.Name || b.FileName || ''));
  }, [models, addModalSearch]);

  return (
    <div>
      <h2>User Access</h2>
      <p className="text-muted">
        Lists every user on the system together with the resources they have access
        to. For each user the <strong>Models</strong> panel shows non-public models
        they have been granted visibility on — use <strong>Add model</strong> to
        grant access or <strong>Remove</strong> on an existing row to revoke it.
        The <strong>Strategies</strong> panel shows the trading strategies the user
        owns; click any row to open it in view mode on the public Trading
        Strategies page. Public models are visible to everyone and do not appear in
        the models panel.
      </p>

      {error && (
        <Alert color="danger">
          <strong>Failed to load data:</strong> {error}
        </Alert>
      )}

      {loading && (
        <div className="text-center py-4">
          <Spinner />
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="mb-2 text-muted small">
            {users.length} user{users.length === 1 ? '' : 's'} • {modelsWithGrants.length}
            {' '}model{modelsWithGrants.length === 1 ? '' : 's'} with explicit grants •
            {' '}{strategiesWithOwners.length}
            {' '}strateg{strategiesWithOwners.length === 1 ? 'y' : 'ies'}
          </div>

          {users.length === 0 && (
            <Alert color="info">No users found.</Alert>
          )}

          {users.map(u => {
            const { id, label, email, roles } = describeUser(u);
            const grants = visibleModelsByUser[id] || [];
            const userStrategies = strategiesByUser[id] || [];
            return (
              <div key={id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{label}</strong>
                    {email && <span className="text-muted ml-2">&lt;{email}&gt;</span>}
                    {' '}<Badge color="secondary">#{id}</Badge>
                    {(roles || []).map(r => (
                      <Badge key={r} color="info" className="ml-1">{r}</Badge>
                    ))}
                  </div>

                </div>
                <div className="card-body">
                  {/* Strategies panel — read-only audit list. Each row is a
                      link to the public TradingStrategyView page so the
                      admin can inspect the strategy in view mode without
                      needing edit / delete access. */}
                  <h6 className="mb-2">
                    Strategies
                    <Badge color="secondary" className="ml-2">{userStrategies.length}</Badge>
                  </h6>
                  {userStrategies.length === 0 ? (
                    <p className="text-muted mb-3">No trading strategies owned by this user.</p>
                  ) : (
                    <table className="table table-sm table-striped mb-4">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Model</th>
                          <th>Connector</th>
                          <th>Signal Tag</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userStrategies.map(s => (
                          <tr key={s.Id}>
                            <td>
                              <Link to={`/trading-strategies/${s.Id}/view`}>
                                {s.Name || `(strategy #${s.Id})`}
                              </Link>
                              {' '}<Badge color="secondary">#{s.Id}</Badge>
                            </td>
                            <td>{s.ModelName || `#${s.ModelId}`}</td>
                            <td>{s.TradingConnectorName || `#${s.TradingConnectorId}`}</td>
                            <td>
                              {s.SignalTag
                                ? <span className="badge bg-info text-dark">{s.SignalTag}</span>
                                : <span className="text-muted">-</span>}
                            </td>
                            <td>
                              {s.Enabled
                                ? <Badge color="success">Enabled</Badge>
                                : <Badge color="secondary">Disabled</Badge>}
                              {s.PendingTrade && (
                                <Badge color="warning" className="ml-1">Pending Trade</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Models panel — existing add/remove grant workflow. */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      Models
                      <Badge color="secondary" className="ml-2">{grants.length}</Badge>
                    </h6>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => {
                        setAddModalError(null);
                        setAddModalSearch('');
                        setAddModalUser(u);
                      }}
                    >
                      Add model
                    </Button>
                  </div>
                  {grants.length === 0 ? (
                    <p className="text-muted mb-0">No visible models granted.</p>
                  ) : (
                    <table className="table table-sm table-striped mb-0">
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>File Name</th>
                          <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grants.map(m => {
                          const revokeKey = `${id}-${m.Id}`;
                          const isRevoking = !!pendingRevoke[revokeKey];
                          return (
                            <tr key={m.Id}>
                              <td>
                                {m.Name || '(unnamed)'}
                                {' '}<Badge color="secondary">#{m.Id}</Badge>
                              </td>
                              <td style={{ wordWrap: 'anywhere' }}>{m.FileName}</td>
                              <td>
                                <Button
                                  color="danger"
                                  size="sm"
                                  disabled={isRevoking}
                                  onClick={() => handleRemove(id, m.Id)}
                                >
                                  {isRevoking ? 'Removing...' : 'Remove'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}

          {/* "Add model" modal — scopes the candidate list to the picked
              user so the dropdown always shows models that are (a) not
              public and (b) not already granted. */}
          {addModalUser && (
            <Modal isOpen toggle={() => setAddModalUser(null)} size="lg">
              <ModalHeader toggle={() => setAddModalUser(null)}>
                Add visible model — {describeUser(addModalUser).label}
              </ModalHeader>
              <ModalBody>
                {addModalError && (
                  <Alert color="danger">{addModalError}</Alert>
                )}
                <Form>
                  <FormGroup>
                    <Label for="user-access-search">Search non-public models</Label>
                    <Input
                      id="user-access-search"
                      type="text"
                      placeholder="Name, file name, or id"
                      value={addModalSearch}
                      onChange={(e) => setAddModalSearch(e.target.value)}
                    />
                  </FormGroup>
                </Form>

                {(() => {
                  const candidates = candidatesForUser(describeUser(addModalUser).id);
                  if (candidates.length === 0) {
                    return (
                      <Alert color="info" className="mb-0">
                        No more non-public models available to grant. Either every
                        non-public model is already granted to this user, or no
                        non-public models exist on the system yet.
                      </Alert>
                    );
                  }
                  return (
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>File Name</th>
                          <th style={{ width: '120px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.map(m => {
                          const userId = describeUser(addModalUser).id;
                          const isGranting = pendingGrant[userId] === m.Id;
                          return (
                            <tr key={m.Id}>
                              <td>
                                {m.Name || '(unnamed)'}
                                {' '}<Badge color="secondary">#{m.Id}</Badge>
                              </td>
                              <td style={{ wordWrap: 'anywhere' }}>{m.FileName}</td>
                              <td>
                                <Button
                                  color="success"
                                  size="sm"
                                  disabled={isGranting}
                                  onClick={() => handleAdd(userId, m.Id)}
                                >
                                  {isGranting ? 'Adding...' : 'Grant'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => setAddModalUser(null)}>
                  Close
                </Button>
              </ModalFooter>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};